import React, { useState } from 'react';

const NewLandingPage = ({ onStartDemo, onLogin, onRegister }) => {
  const [businessExample, setBusinessExample] = useState('');

  // Example businesses for the demo selector
  const businessExamples = [
    { type: 'coach', icon: '🎯', title: 'Coach professionnel', desc: 'Accompagnement et transformation' },
    { type: 'ecommerce', icon: '🛒', title: 'E-commerce', desc: 'Boutique en ligne' },
    { type: 'artisan', icon: '🔨', title: 'Artisan local', desc: 'Services ou produits' },
    { type: 'consultant', icon: '💼', title: 'Consultant B2B', desc: 'Expertise et conseil' },
    { type: 'restaurant', icon: '🍽️', title: 'Restaurant', desc: 'Restauration et food' },
    { type: 'saas', icon: '💻', title: 'SaaS / Startup', desc: 'Produit digital' },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0f]" data-testid="new-landing">
      {/* Minimal Header */}
      <header className="fixed top-0 w-full z-50 bg-[#0a0a0f]/80 backdrop-blur-xl border-b border-gray-800/50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <img src="/vfa-logo.png" alt="VF Actions" className="h-8" />
            <span className="text-white font-bold hidden sm:block">VF ACTIONS</span>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={onLogin}
              className="px-4 py-2 text-gray-400 hover:text-white transition-colors text-sm"
              data-testid="landing-login"
            >
              Connexion
            </button>
            <button
              onClick={onRegister}
              className="px-4 py-2 bg-green-500/20 hover:bg-green-500/30 border border-green-500/30 text-green-400 rounded-lg text-sm font-medium transition-all"
              data-testid="landing-register"
            >
              Créer un compte
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section - Value Proposition */}
      <section className="relative min-h-screen flex items-center justify-center pt-20 pb-16 overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-green-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-emerald-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-teal-500/5 rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 text-center">
          {/* Badge */}
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-green-500/10 border border-green-500/20 text-green-400 text-sm mb-8">
            <span className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></span>
            Assistant Marketing IA pour Entrepreneurs
          </div>

          {/* Main Headline */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
            Décrivez votre activité,
            <br />
            <span className="bg-gradient-to-r from-green-400 via-emerald-400 to-teal-400 bg-clip-text text-transparent">
              obtenez votre stratégie marketing
            </span>
          </h1>

          {/* Sub-headline */}
          <p className="text-lg sm:text-xl text-gray-400 mb-12 max-w-3xl mx-auto leading-relaxed">
            En quelques minutes, l'IA analyse votre activité et génère automatiquement 
            votre <strong className="text-white">avatar client</strong>, votre <strong className="text-white">positionnement</strong>, 
            vos <strong className="text-white">arguments de vente</strong>, vos <strong className="text-white">emails</strong>, 
            vos <strong className="text-white">publicités</strong> et votre <strong className="text-white">calendrier éditorial</strong>.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <button
              onClick={() => onStartDemo()}
              className="px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-xl font-semibold text-lg transition-all shadow-lg shadow-green-500/30 flex items-center justify-center space-x-2"
              data-testid="landing-cta-demo"
            >
              <span>Tester gratuitement</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
              </svg>
            </button>
            <button
              onClick={onLogin}
              className="px-8 py-4 bg-gray-800 hover:bg-gray-700 text-white rounded-xl font-medium text-lg transition-all border border-gray-700"
              data-testid="landing-cta-login"
            >
              J'ai déjà un compte
            </button>
          </div>

          {/* Trust indicators */}
          <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-500">
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
              </svg>
              <span>Sans carte bancaire</span>
            </div>
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
              </svg>
              <span>Résultats en 2 minutes</span>
            </div>
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
              </svg>
              <span>100% personnalisé</span>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 14l-7 7m0 0l-7-7m7 7V3"/>
          </svg>
        </div>
      </section>

      {/* What You Get Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-[#0a0a0f] to-gray-900/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-green-400 text-sm font-mono uppercase tracking-wider">Ce que vous obtenez</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-4 mb-4">
              Une stratégie marketing complète
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Plus besoin de consultant ou d'agence. L'IA fait le travail de plusieurs experts en quelques minutes.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: '👤',
                title: 'Avatar Client',
                description: 'Comprenez parfaitement qui est votre client idéal, ses frustrations, ses désirs et ses objections.',
                badge: 'Étape 1'
              },
              {
                icon: '🎯',
                title: 'Positionnement',
                description: 'Définissez votre proposition de valeur unique et différenciez-vous clairement de la concurrence.',
                badge: 'Étape 2'
              },
              {
                icon: '💡',
                title: 'Arguments de Vente',
                description: 'Obtenez 5+ arguments de vente percutants qui parlent directement à vos clients.',
                badge: 'Étape 3'
              },
              {
                icon: '📄',
                title: 'Page de Vente',
                description: 'Une page de vente complète avec accroches, bénéfices, preuves et appels à l\'action.',
                badge: 'Contenu'
              },
              {
                icon: '✉️',
                title: 'Séquence Email',
                description: '5 emails de nurturing pour transformer vos prospects en clients fidèles.',
                badge: 'Contenu'
              },
              {
                icon: '📱',
                title: 'Publicités',
                description: '3 publicités Facebook et 3 publicités Google prêtes à diffuser.',
                badge: 'Contenu'
              },
              {
                icon: '📲',
                title: 'Posts Social Media',
                description: '10 publications engageantes pour vos réseaux sociaux.',
                badge: 'Contenu'
              },
              {
                icon: '📅',
                title: 'Calendrier Éditorial',
                description: 'Un planning de 30 jours avec tous vos contenus organisés.',
                badge: 'Planning'
              },
              {
                icon: '🔄',
                title: 'Humanisation IA',
                description: 'Tous les contenus passent par notre moteur d\'humanisation pour un rendu naturel.',
                badge: 'Qualité'
              },
            ].map((item, i) => (
              <div 
                key={i}
                className="group p-6 bg-gray-900/30 rounded-2xl border border-gray-800 hover:border-green-500/30 transition-all hover:bg-gray-900/50"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500/20 to-emerald-500/10 flex items-center justify-center text-2xl">
                    {item.icon}
                  </div>
                  <span className="text-xs px-2 py-1 bg-gray-800 text-gray-400 rounded-full">
                    {item.badge}
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-sm text-gray-400">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-green-400 text-sm font-mono uppercase tracking-wider">Comment ça marche</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-4 mb-4">
              Simple comme 1, 2, 3
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '1',
                title: 'Décrivez votre activité',
                description: 'En quelques phrases, expliquez ce que vous faites et qui sont vos clients.',
                icon: '✍️'
              },
              {
                step: '2',
                title: 'L\'IA analyse et crée',
                description: 'Notre moteur IA analyse votre marché et génère votre stratégie complète.',
                icon: '🧠'
              },
              {
                step: '3',
                title: 'Utilisez vos contenus',
                description: 'Copiez, modifiez et publiez vos contenus marketing immédiatement.',
                icon: '🚀'
              },
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="relative inline-block mb-6">
                  <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-green-500/20 to-emerald-500/10 border border-green-500/30 flex items-center justify-center text-4xl">
                    {item.icon}
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-green-500 text-white font-bold flex items-center justify-center text-sm">
                    {item.step}
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-gray-400">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* For Who Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-gray-900/50 to-[#0a0a0f]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-green-400 text-sm font-mono uppercase tracking-wider">Pour qui ?</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-4 mb-4">
              Parfait pour les entrepreneurs
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Que vous démarriez ou que vous souhaitiez relancer votre marketing, VF Actions s'adapte à votre activité.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {businessExamples.map((biz, i) => (
              <button
                key={i}
                onClick={() => onStartDemo(biz.type)}
                className="p-4 bg-gray-900/30 rounded-xl border border-gray-800 hover:border-green-500/50 hover:bg-green-500/10 transition-all text-center group"
                data-testid={`landing-biz-${biz.type}`}
              >
                <span className="text-3xl block mb-2 group-hover:scale-110 transition-transform">{biz.icon}</span>
                <span className="text-white font-medium text-sm block">{biz.title}</span>
                <span className="text-gray-500 text-xs block mt-1">{biz.desc}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-3xl border border-green-500/20 p-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Prêt à transformer votre marketing ?
            </h2>
            <p className="text-gray-400 mb-8 text-lg">
              Testez gratuitement et obtenez votre première stratégie marketing en quelques minutes.
            </p>
            <button
              onClick={() => onStartDemo()}
              className="px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-xl font-semibold text-lg transition-all shadow-lg shadow-green-500/30"
              data-testid="landing-final-cta"
            >
              Créer ma stratégie gratuitement
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-gray-800">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center">
          <div className="flex items-center space-x-2 mb-4 md:mb-0">
            <img src="/vfa-logo.png" alt="VF Actions" className="h-6" />
            <span className="text-gray-500 text-sm">© 2025 VF Actions</span>
          </div>
          <div className="flex space-x-6 text-sm text-gray-500">
            <a href="#" className="hover:text-white transition-colors">Mentions légales</a>
            <a href="#" className="hover:text-white transition-colors">Confidentialité</a>
            <a href="#" className="hover:text-white transition-colors">CGU</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default NewLandingPage;
